// FILE: ProductDetails/ProductDetailsSkeletons.jsx
// Skeleton loading components for product details page

import React from "react";

// Shimmer animation component
const Shimmer = ({ className = "" }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);

// Product Hero Skeleton
export const ProductHeroSkeleton = () => (
  <div className="bg-white rounded-xl lg:rounded-2xl shadow-sm overflow-hidden mb-4 lg:mb-6">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 lg:gap-6">
      {/* Image Gallery Skeleton */}
      <div className="relative bg-gray-100">
        <div className="aspect-square lg:aspect-[4/3]">
          <Shimmer className="w-full h-full rounded-none" />
        </div>
        {/* Thumbnail Strip */}
        <div className="flex gap-2 p-3">
          {[...Array(4)].map((_, i) => (
            <Shimmer key={i} className="flex-shrink-0 w-16 h-16 lg:w-20 lg:h-20 rounded-lg" />
          ))}
        </div>
      </div>

      {/* Product Info Skeleton */}
      <div className="p-4 lg:p-6 lg:py-8">
        {/* Title & IDs */}
        <div className="mb-4 lg:mb-6">
          <Shimmer className="h-7 lg:h-8 w-3/4 mb-3" />
          <div className="flex flex-wrap gap-2">
            <Shimmer className="h-6 w-24 rounded" />
            <Shimmer className="h-6 w-28 rounded" />
            <Shimmer className="h-6 w-20 rounded" />
          </div>
        </div>

        {/* Quick Stats Grid */}
        <div className="grid grid-cols-2 gap-3 lg:gap-4 mb-4 lg:mb-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-gray-100 rounded-xl p-3 lg:p-4">
              <Shimmer className="h-3 w-12 mb-2" />
              <Shimmer className="h-5 w-20" />
            </div>
          ))}
        </div>

        {/* Stock Summary */}
        <div className="bg-gray-50 rounded-xl p-4 mb-4 lg:mb-6">
          <div className="flex justify-between mb-3">
            <Shimmer className="h-5 w-28" />
            <Shimmer className="h-4 w-20" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="text-center">
                <Shimmer className="h-6 w-10 mx-auto mb-1" />
                <Shimmer className="h-3 w-14 mx-auto" />
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="hidden lg:flex gap-3">
          <Shimmer className="h-10 flex-1 rounded-lg" />
          <Shimmer className="h-10 w-28 rounded-lg" />
        </div>
      </div>
    </div>
  </div>
);

// Tabs Section Skeleton
export const TabsSkeleton = () => (
  <div className="bg-white rounded-xl lg:rounded-2xl shadow-sm overflow-hidden">
    {/* Tab Navigation */}
    <div className="border-b">
      <div className="flex">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="px-4 lg:px-6 py-3 lg:py-4">
            <Shimmer className="h-5 w-20" />
          </div>
        ))}
      </div>
    </div>

    {/* Tab Content */}
    <div className="p-4 lg:p-6">
      <OverviewTabSkeleton />
    </div>
  </div>
);

// Overview Tab Skeleton
export const OverviewTabSkeleton = () => (
  <div className="space-y-4 lg:space-y-6">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
      {/* Basic Details Card */}
      <div className="bg-gray-50 rounded-xl p-4 lg:p-5">
        <Shimmer className="h-5 w-32 mb-4" />
        <div className="space-y-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="flex justify-between">
              <Shimmer className="h-4 w-20" />
              <Shimmer className="h-4 w-24" />
            </div>
          ))}
        </div>
      </div>

      {/* Weight Details Card */}
      <div className="bg-gray-50 rounded-xl p-4 lg:p-5">
        <Shimmer className="h-5 w-32 mb-4" />
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex justify-between">
              <Shimmer className="h-4 w-24" />
              <Shimmer className="h-4 w-16" />
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Manufacturing Details */}
    <div className="bg-gray-50 rounded-xl p-4 lg:p-5">
      <Shimmer className="h-5 w-32 mb-4" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i}>
            <Shimmer className="h-3 w-20 mb-2" />
            <Shimmer className="h-5 w-24" />
          </div>
        ))}
      </div>
    </div>

    {/* Branch Availability */}
    <div className="bg-gray-50 rounded-xl p-4 lg:p-5">
      <Shimmer className="h-5 w-36 mb-4" />
      <div className="flex flex-wrap gap-2">
        {[...Array(4)].map((_, i) => (
          <Shimmer key={i} className="h-8 w-28 rounded-lg" />
        ))}
      </div>
    </div>
  </div>
);

// Stock Tab Skeleton
export const StockTabSkeleton = () => (
  <div className="space-y-4 lg:space-y-6">
    {/* Stock Overview Cards */}
    <div className="grid grid-cols-3 gap-3 lg:gap-4">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="bg-gray-100 rounded-xl p-4 text-center">
          <Shimmer className="w-10 h-10 lg:w-12 lg:h-12 rounded-full mx-auto mb-2" />
          <Shimmer className="h-6 w-12 mx-auto mb-1" />
          <Shimmer className="h-3 w-16 mx-auto" />
        </div>
      ))}
    </div>

    {/* Stock Levels */}
    <div className="bg-gray-50 rounded-xl p-4 lg:p-5">
      <Shimmer className="h-5 w-28 mb-4" />
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <Shimmer className="h-3 w-32 mb-2" />
          <Shimmer className="h-6 w-12" />
        </div>
        <div>
          <Shimmer className="h-3 w-32 mb-2" />
          <Shimmer className="h-6 w-16" />
        </div>
      </div>
      <Shimmer className="h-2 w-full rounded-full" />
    </div>

    {/* Location Cards */}
    <div>
      <div className="flex justify-between mb-4">
        <Shimmer className="h-5 w-36" />
        <Shimmer className="h-8 w-24 rounded-lg" />
      </div>
      <div className="space-y-3">
        {[...Array(2)].map((_, i) => (
          <div key={i} className="bg-white border rounded-xl p-4">
            <div className="flex justify-between items-start">
              <div>
                <Shimmer className="h-5 w-32 mb-2" />
                <Shimmer className="h-3 w-20" />
              </div>
              <div className="flex gap-4">
                {[...Array(3)].map((_, j) => (
                  <div key={j} className="text-center">
                    <Shimmer className="h-6 w-8 mx-auto mb-1" />
                    <Shimmer className="h-3 w-12" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Pricing Tab Skeleton
export const PricingTabSkeleton = () => (
  <div className="space-y-4 lg:space-y-6">
    {/* Customer Type Badge */}
    <div className="flex items-center gap-2">
      <Shimmer className="h-6 w-28 rounded-full" />
      <Shimmer className="h-4 w-32" />
    </div>

    {/* Summary Cards */}
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="bg-gray-100 rounded-xl p-4">
          <Shimmer className="w-8 h-8 rounded-lg mb-2" />
          <Shimmer className="h-3 w-20 mb-2" />
          <Shimmer className="h-7 w-24" />
        </div>
      ))}
    </div>

    {/* Branch Comparison */}
    <div className="bg-white border rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-4 lg:px-6 py-4 bg-gray-50 border-b">
        <div className="flex justify-between items-center">
          <Shimmer className="h-6 w-40" />
          <div className="flex gap-2">
            <Shimmer className="h-8 w-28 rounded-lg" />
            <Shimmer className="h-8 w-8 rounded-lg" />
          </div>
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden divide-y">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="p-4">
            <div className="flex justify-between items-center">
              <div>
                <Shimmer className="h-5 w-32 mb-1" />
                <Shimmer className="h-5 w-24" />
              </div>
              <Shimmer className="h-5 w-5" />
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              {[...Array(11)].map((_, i) => (
                <th key={i} className="px-4 py-3">
                  <Shimmer className="h-4 w-16" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...Array(3)].map((_, i) => (
              <tr key={i} className="border-t">
                {[...Array(11)].map((_, j) => (
                  <td key={j} className="px-4 py-4">
                    <Shimmer className="h-5 w-16" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

// History Tab Skeleton
export const HistoryTabSkeleton = () => (
  <div className="space-y-4 lg:space-y-6">
    {/* Timeline */}
    <div className="space-y-4">
      {[...Array(2)].map((_, i) => (
        <div key={i} className="flex gap-4">
          <Shimmer className="w-10 h-10 rounded-full flex-shrink-0" />
          <div className="flex-1 bg-gray-50 rounded-xl p-4">
            <div className="flex justify-between items-start">
              <div>
                <Shimmer className="h-5 w-32 mb-2" />
                <Shimmer className="h-4 w-40" />
              </div>
              <Shimmer className="h-4 w-36" />
            </div>
          </div>
        </div>
      ))}
    </div>

    {/* Stats Card */}
    <div className="bg-gray-50 rounded-xl p-4 lg:p-5">
      <Shimmer className="h-5 w-36 mb-4" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="text-center p-3 bg-white rounded-lg">
            <Shimmer className="h-6 w-10 mx-auto mb-1" />
            <Shimmer className="h-3 w-16 mx-auto" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Full Page Skeleton
export const ProductDetailsSkeleton = () => (
  <div className="min-h-screen bg-gray-50">
    {/* Mobile Header */}
    <div className="lg:hidden sticky top-0 z-20 bg-white border-b shadow-sm">
      <div className="flex items-center justify-between p-4">
        <Shimmer className="w-9 h-9 rounded-lg" />
        <Shimmer className="h-5 w-40" />
        <Shimmer className="w-9 h-9 rounded-lg" />
      </div>
    </div>

    <div className="container mx-auto px-4 py-4 lg:py-6 max-w-7xl">
      {/* Desktop Back Button */}
      <div className="hidden lg:block mb-6">
        <Shimmer className="h-5 w-32" />
      </div>

      <ProductHeroSkeleton />
      <TabsSkeleton />
    </div>

    {/* Mobile Bottom Bar */}
    <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4">
      <div className="flex gap-3">
        <Shimmer className="h-10 flex-1 rounded-lg" />
        <Shimmer className="h-10 w-12 rounded-lg" />
      </div>
    </div>
  </div>
);

// Inline Spinner
export const InlineSpinner = ({ size = 16, className = "" }) => (
  <div
    className={`border-2 border-current border-t-transparent rounded-full animate-spin ${className}`}
    style={{ width: size, height: size }}
  />
);

// Section Loading Overlay
export const SectionLoadingOverlay = ({ message = "Loading..." }) => (
  <div className="absolute inset-0 bg-white/80 backdrop-blur-[1px] flex items-center justify-center z-10 rounded-xl">
    <div className="flex flex-col items-center gap-2">
      <InlineSpinner size={24} className="text-primary" />
      <span className="text-sm text-gray-600">{message}</span>
    </div>
  </div>
);

// Empty State Component
export const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="text-center py-8 lg:py-12">
    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
      <Icon className="w-8 h-8 text-gray-400" />
    </div>
    <p className="text-gray-600 font-medium mb-2">{title}</p>
    {description && <p className="text-sm text-gray-500 mb-4">{description}</p>}
    {action}
  </div>
);

export default {
  ProductHeroSkeleton,
  TabsSkeleton,
  OverviewTabSkeleton,
  StockTabSkeleton,
  PricingTabSkeleton,
  HistoryTabSkeleton,
  ProductDetailsSkeleton,
  InlineSpinner,
  SectionLoadingOverlay,
  EmptyState,
};
